import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Loader2 } from "lucide-react"
import type { NoteOut } from "../../../api/notesApi"
import { formatDate } from "../../../utils/date"
import { NoteAttachmentList } from "./NoteAttachmentList"
import { NoteText } from "./NoteText"

/** One inquiry the send was refused for, with the notes behind the refusal. */
export interface SupplierWarningEntry {
  inquiryId: string
  supplierName: string
  notes: NoteOut[]
}

interface SupplierWarningDialogProps {
  /** "single" names one supplier; "bulk" lists every supplier a batch was refused for. */
  mode: "single" | "bulk"
  warnings: SupplierWarningEntry[]
  /** Send anyway: the caller retries with the notes' ids as acknowledged. */
  onConfirm: () => void
  /** Bulk only: leave the flagged inquiries as drafts and carry on with the rest. */
  onSkip?: () => void
  onCancel: () => void
  isLoading?: boolean
}

/**
 * Shown when a send comes back 409 SUPPLIER_WARNING: nothing was sent for those
 * inquiries yet. The user reads the supplier's warning notes and decides.
 */
export function SupplierWarningDialog({
  mode,
  warnings,
  onConfirm,
  onSkip,
  onCancel,
  isLoading = false,
}: SupplierWarningDialogProps) {
  const { translate } = useTranslation()
  const bulk = mode === "bulk"

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isLoading) onCancel()
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {bulk
              ? translate("MC_NOTE_WARN_DIALOG_BULK_TITLE", {
                  count: warnings.length,
                })
              : translate("MC_NOTE_WARN_DIALOG_TITLE")}
          </DialogTitle>
          <DialogDescription>
            {bulk
              ? translate("MC_NOTE_WARN_DIALOG_BULK_DESC")
              : translate("MC_NOTE_WARN_DIALOG_DESC", {
                  supplier: warnings[0]?.supplierName ?? "",
                })}
          </DialogDescription>
        </DialogHeader>

        <ul className="flex flex-col gap-4">
          {warnings.map((warning) => (
            <li key={warning.inquiryId} className="flex flex-col gap-2">
              {bulk && (
                <div className="text-[13px] font-medium text-white">
                  {warning.supplierName}
                </div>
              )}
              <ul className="flex flex-col gap-2">
                {warning.notes.map((note) => (
                  <li
                    key={note.id}
                    className="flex flex-col gap-2 rounded-md border border-grey4 bg-grey3 p-3"
                  >
                    <div className="text-[11.5px] text-grey5">
                      <span className="font-medium text-white">
                        {note.createdBy}
                      </span>{" "}
                      {formatDate(note.createdAt)}
                    </div>
                    <p className="text-[13px] text-white">
                      <NoteText body={note.body} quoted />
                    </p>
                    <NoteAttachmentList attachments={note.attachments} />
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>

        {/* Wraps instead of widening the dialog: the German labels are long. */}
        <DialogFooter className="flex-wrap gap-2 sm:space-x-0">
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            {translate("MC_DECL_BTN_CANCEL")}
          </Button>
          {bulk && onSkip && (
            <Button variant="outline" onClick={onSkip} disabled={isLoading}>
              {translate("MC_NOTE_WARN_SKIP")}
            </Button>
          )}
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading && <Loader2 size={14} className="animate-spin" />}
            {translate(
              bulk
                ? "MC_NOTE_WARN_SEND_ALL_ANYWAY"
                : "MC_NOTE_WARN_SEND_ANYWAY",
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
